import { Consent } from '@axo/form/feature/axo-form-v2/lib/components';
import { useTranslation } from '@axo/shared/i18n';
import { ButtonSet } from '@axo/ui-core/components/Button';
import { Grid } from '@axo/ui-core/components/layout/item';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from '../../../../../components/Form';
import { Navigation } from '../../../../../components/Navigation';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import useInitiatedTracking from '../../../../../hooks/tracking/useInitiatedTracking';
import {
  getMarketingConsentDefaultValues,
  useMarketingConsent,
} from '../../../../../hooks/useMarketingConsent';
import { gridColumns } from '../../config/layouts/grid';
import { Step5Data } from '../../FormData.se.types';
import { PurposeFormGroup } from '../shared/PurposeFormGroup';
import { AccountFormGroup } from './AccountFormGroup';
import { CoApplicant } from './CoApplicant';

export const Step5 = () => {
  const { t } = useTranslation();

  const { onChange, onPrevStep, onSubmit, axoFormData } =
    useAxoFormContext<Step5Data>();

  const form = useForm({
    defaultValues: {
      'Frontend/AddAccountNumber': 'No',
      'LoanApplication/Purpose': axoFormData['LoanApplication/Purpose'],
      ...getMarketingConsentDefaultValues({
        marketingConsent: axoFormData['Ignore/MarketingConsent'],
        showMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
        defaultValueOverride: false,
      }),
    },
    shouldUnregister: true,
    mode: 'onBlur',
  });

  const {
    formState: { isSubmitting, touchedFields },
  } = form;

  useInitiatedTracking({
    shouldTriggerOnInitiated: Object.keys(touchedFields).length > 0,
  });

  const { showMarketingConsent = true } = useMarketingConsent({
    marketingConsent: axoFormData['Ignore/MarketingConsent'],
    shouldShowMarketingConsent: form.getValues('Ignore/ShowMarketingConsent'),
  });

  const showPurpose =
    !axoFormData['LoanApplication/Purpose'] ||
    !!touchedFields['LoanApplication/Purpose'];

  const touchedFieldsOverride = useMemo(
    () =>
      Object.keys(axoFormData).filter((key) =>
        key.match(/CoApp|CoPerson|CoCustomer/)
      ),
    [axoFormData]
  );

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={onSubmit}
        onChange={onChange}
        touchedFieldsOverride={touchedFieldsOverride}
      >
        {showPurpose && (
          <Grid columns={gridColumns}>
            <PurposeFormGroup />
          </Grid>
        )}

        <AccountFormGroup />

        <CoApplicant />

        <ButtonSet justify={'space-between'} responsive>
          <Navigation.Back onClick={onPrevStep}>{t('back')}</Navigation.Back>
          <Navigation.Continue loading={isSubmitting}>
            {t('submit', { context: 'form' })}
          </Navigation.Continue>
        </ButtonSet>
      </Form>

      <Consent showMarketingConsent={showMarketingConsent}>
        <Consent.TermsAndConditions />
        <Consent.Marketing />
      </Consent>
    </FormProvider>
  );
};
